import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../store';
import { Moment } from 'moment';
import { PackageStatusEnum } from '../../pages/ConveyorMonitor/OrderCard/utils';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { ConveyorOrderPackage } from '../../pages/ConveyorMonitor/model';

export interface UpdateIncompleteConveyorPackageProps {
  pkgId: number;
  pkgStatusId: number;
  connection: HubConnection | null;
}

export interface IncompleteConveyorPackage {
  selectedShipperSpec: number;
  netsuiteSoId: number;
  packageNumber: number;
  conveyorPackageId: number;
  itemName: string;
  conveyorPackageStatus: PackageStatusEnum;
  isPrintAndLabel?: boolean;
  printDate?: Moment;
  reprintDate?: Moment;
  expiryDate?: Moment;
  userName?: string;
}

export interface CloseoutPackagesProps {
  conveyorPackages: IncompleteConveyorPackage[];
  manualLabelPackages: ManualLabelPackageProps[];
  loadingManualPackages: boolean;
  searchOrderNumb: string;
  searchItemName: string;
}

export enum ShipmentPackageStatus {
  UNKNOWN = 0,
  VERIFIED = 1,
  READY_TO_RATE_SHOP = 2,
  RATE_SHOPPING = 3,
  READY_TO_LABEL = 4,
  LABELED = 5,
  SHIPPED = 6,
  CANCELED = 7,
  IN_ERROR = 8,
  BAG_SIZE_SELECTED = 9,
}

export interface ManualLabelPackageProps {
  SalesOrderId: number;
  NetsuiteId: number;
  PackageNumber: number;
  ShipmentPackageStatusId: ShipmentPackageStatus;
  ItemName: string;
  isPrintAndLabel?: boolean;
  printDate?: Moment;
  reprintDate?: Moment;
  expiryDate?: Moment;
  userName?: string;
}

// Define the initial state using that type
const initialState: CloseoutPackagesProps = {
  conveyorPackages: [],
  manualLabelPackages: [],
  loadingManualPackages: false,
  searchOrderNumb: '',
  searchItemName: '',
};

export const packagesSlice = createSlice({
  name: 'packages',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    refreshIncompleteConveyorPackages: (state, action: PayloadAction<ConveyorOrderPackage[]>) => {
      state.conveyorPackages.forEach((conveyorPackage, index) => {
        let foundIndex = action.payload.findIndex(
          (x) => x.packageId === conveyorPackage.conveyorPackageId,
        );
        if (foundIndex === -1) {
          state.conveyorPackages.splice(index, 1);
        } else {
          let statusToSet = action.payload[foundIndex].packageStatusId;
          if (statusToSet === PackageStatusEnum.DIVERTED_SUCCESS) {
            state.conveyorPackages.splice(index, 1);
          } else {
            conveyorPackage.conveyorPackageStatus = action.payload[foundIndex].packageStatusId;
          }
        }
      });
    },
    setIncompleteConveyorPackages: (state, action: PayloadAction<IncompleteConveyorPackage[]>) => {
      action.payload.forEach((item) => {
        let index = state.conveyorPackages.findIndex(
          (x) =>
            x.conveyorPackageId === item.conveyorPackageId && x.netsuiteSoId === item.netsuiteSoId,
        );
        if (index === -1) {
          state.conveyorPackages.push(item);
        } else {
          state.conveyorPackages[index] = item;
        }
      });
    },
    setSearchOrderNumb: (state, action: PayloadAction<string>) => {
      state.searchOrderNumb = action.payload;
    },
    setSearchItemName: (state, action: PayloadAction<string>) => {
      state.searchItemName = action.payload;
    },
    setLoadingManualPackages: (state, action: PayloadAction<boolean>) => {
      state.loadingManualPackages = action.payload;
    },
    setManualLabelPackages: (state, action: PayloadAction<ManualLabelPackageProps[]>) => {
      state.manualLabelPackages = action.payload;
    },
    updateIncompletePackageStatus: (
      state,
      action: PayloadAction<UpdateIncompleteConveyorPackageProps>,
    ) => {
      const { pkgId, pkgStatusId, connection } = action.payload;
      let index = state.conveyorPackages.findIndex((x) => x.conveyorPackageId === pkgId);
      if (index !== -1) {
        if (pkgStatusId === PackageStatusEnum.DIVERTED_SUCCESS) {
          state.conveyorPackages.splice(index, 1);
          console.log(`Removing diverted success item ${pkgId}`);
        } else {
          console.log(
            `Updating Package ${pkgId} from ${state.conveyorPackages[index].conveyorPackageStatus} to ${pkgStatusId}`,
          );
          state.conveyorPackages[index].conveyorPackageStatus = pkgStatusId;
        }

        if (connection && connection.state === HubConnectionState.Connected) {
          console.log(connection);
          try {
            console.log('Invoking method from the server to update Cache');
            connection.invoke('UpdatePackageStatusCache', pkgId, pkgStatusId);
          } catch (err) {
            console.log('Failed to update cache on the server');
          }
        }
      }
    },
  },
});

export const {
  refreshIncompleteConveyorPackages,
  setManualLabelPackages,
  setLoadingManualPackages,
  setIncompleteConveyorPackages,
  updateIncompletePackageStatus,
  setSearchItemName,
  setSearchOrderNumb,
} = packagesSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectIncompleteConveyorPackages = (state: RootState) =>
  state.packages.conveyorPackages;
export const selectManualLabelPackages = (state: RootState) => state.packages.manualLabelPackages;
export const selectLoadingManualPackages = (state: RootState) =>
  state.packages.loadingManualPackages;
export const selectSearchItemName = (state: RootState) => state.packages.searchItemName;
export const selectSearchOrderNumb = (state: RootState) => state.packages.searchOrderNumb;

export default packagesSlice.reducer;
